// Filtro por grupo da busca de produtos.
//
// O grupo vem do ERP dentro de cada item do estoque (`grupoId`/`grupoNome` e, quando o grupo tem
// pai, `grupoPaiId`/`grupoPaiNome`) — não há endpoint separado de grupos. A lista de opções é
// derivada dos produtos já carregados, então ela só oferece grupos que de fato têm produto naquela
// filial, e trocar de filtro não custa nenhuma request.
//
// A hierarquia do ERP é de dois níveis: todo filtro por grupo de lá é `grup_id = X OR grup_pai = X`.
// Por isso um produto casa com o grupo selecionado quando é o grupo dele **ou** o pai dele — é o que
// faz selecionar um grupo pai trazer os produtos dos filhos.

const normalizarId = (valor) => {
  if (valor === null || valor === undefined || valor === "") return null;
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : null;
};

const compararPorNome = (a, b) =>
  (a.nome || "").localeCompare(b.nome || "", "pt-BR", { sensitivity: "base" });

const registrar = (mapa, id, nome, paiId) => {
  if (id == null) return;
  const atual = mapa.get(id);
  if (!atual) {
    mapa.set(id, { id, nome: nome || `Grupo ${id}`, paiId });
    return;
  }
  if (paiId != null && atual.paiId == null) atual.paiId = paiId;
  if (!atual.nome && nome) atual.nome = nome;
};

/**
 * Monta as opções do filtro a partir dos produtos carregados.
 *
 * Devolve uma lista já na ordem de exibição — cada pai seguido dos filhos dele — com `nivel`
 * (0 = pai/raiz, 1 = filho) para a tela indentar.
 */
export const montarOpcoesGrupos = (produtos) => {
  if (!Array.isArray(produtos) || produtos.length === 0) return [];

  const mapa = new Map();
  for (const produto of produtos) {
    if (!produto) continue;
    const id = normalizarId(produto.grupoId);
    const paiId = normalizarId(produto.grupoPaiId);
    registrar(mapa, id, produto.grupoNome, paiId);
    registrar(mapa, paiId, produto.grupoPaiNome, null);
  }

  const todos = [...mapa.values()];
  const filhosPorPai = new Map();
  const raizes = [];

  for (const grupo of todos) {
    // Guarda: pai que não está entre as opções viraria um filho pendurado em nada, e sumiria da
    // tela. Nesse caso o grupo entra como raiz.
    if (grupo.paiId != null && mapa.has(grupo.paiId)) {
      const irmaos = filhosPorPai.get(grupo.paiId) || [];
      irmaos.push(grupo);
      filhosPorPai.set(grupo.paiId, irmaos);
    } else {
      raizes.push(grupo);
    }
  }

  const opcoes = [];
  for (const raiz of raizes.sort(compararPorNome)) {
    opcoes.push({ id: raiz.id, nome: raiz.nome, paiId: null, nivel: 0 });
    const filhos = filhosPorPai.get(raiz.id) || [];
    for (const filho of filhos.sort(compararPorNome)) {
      opcoes.push({ id: filho.id, nome: filho.nome, paiId: raiz.id, nivel: 1 });
    }
  }
  return opcoes;
};

/** Produtos dos grupos selecionados (e dos filhos deles). Sem seleção, devolve a lista original. */
export const filtrarProdutosPorGrupos = (produtos, idsSelecionados) => {
  if (!Array.isArray(produtos)) return [];
  if (!Array.isArray(idsSelecionados) || idsSelecionados.length === 0) return produtos;

  const selecionados = new Set(idsSelecionados.map(normalizarId).filter((id) => id != null));
  if (selecionados.size === 0) return produtos;

  return produtos.filter((produto) => {
    if (!produto) return false;
    const id = normalizarId(produto.grupoId);
    const paiId = normalizarId(produto.grupoPaiId);
    return (id != null && selecionados.has(id)) || (paiId != null && selecionados.has(paiId));
  });
};

/**
 * Opções que casam com o texto digitado, na mesma semântica da busca de produtos do `MessageInput`:
 * termos separados por espaço, todos precisam aparecer no nome.
 *
 * A hierarquia é preservada nos dois sentidos, senão a lista filtrada mentiria sobre o que existe:
 * pai que casa traz os filhos dele (é o que se espera ao buscar "PNEUS"), e filho que casa traz o
 * pai junto, como contexto — sem ele o filho apareceria solto, indentado embaixo de nada.
 *
 * O pai trazido como contexto continua clicável, e marcá-lo seleciona o grupo inteiro, inclusive os
 * filhos que a busca escondeu: quem manda na seleção é o grupo, não o texto digitado no momento.
 */
export const filtrarOpcoesPorTexto = (opcoes, texto) => {
  if (!Array.isArray(opcoes)) return [];
  const termos = (texto || "").toLowerCase().split(" ").filter(Boolean);
  if (termos.length === 0) return opcoes;

  const casa = (opcao) => {
    const nome = (opcao.nome || "").toLowerCase();
    return termos.every((termo) => nome.includes(termo));
  };

  const idsQueCasam = new Set(opcoes.filter(casa).map((o) => o.id));
  const paisComFilhoQueCasa = new Set(
    opcoes.filter((o) => o.paiId != null && idsQueCasam.has(o.id)).map((o) => o.paiId)
  );

  return opcoes.filter(
    (opcao) =>
      idsQueCasam.has(opcao.id) ||
      paisComFilhoQueCasa.has(opcao.id) ||
      (opcao.paiId != null && idsQueCasam.has(opcao.paiId))
  );
};
